import { FolderType } from "@/types/productTypes";
import { ImageType } from "@/types/productTypes";
import { ConnectDropTarget, useDrop } from "react-dnd";
import { useAdminStore } from "../../../../store/useAdminStore";
import { ChevronUp } from "lucide-react";
import { Button } from "@/Components/ui/button";
import { ChevronLeft } from "lucide-react";
import Breadcrumbs from "../BreadCrumbs";
import ImageCard from "./ImageCard";
import FolderCard from "../folder/FolderCard";

type ImageGridProps = {
   images: ImageType[];
   folders: FolderType[];
   onDelete: (id: number) => void;
   onDeleteFolder: (id: number) => void;
   onEdit: (image: ImageType) => void;
   onEditFolder: (data: Partial<FolderType>) => Promise<void>;
   onNavigate: (folderId: number | null) => void;
   currentFolderId: number | null;
};

// -------------------------------------------------------
// Update the ImageGrid component
export default function ImageGrid({
   folders,
   images,
   onDelete,
   onDeleteFolder,
   onEdit,
   onEditFolder,
   onNavigate,
   currentFolderId,
}: ImageGridProps) {
   const { assignImageToFolder, allImages, setOptimisticImages } =
      useAdminStore();

   // Add drop target for the parent folder area
   const [{ isOver, canDrop }, dropToParent] = useDrop(
      () => ({
         accept: "IMAGE",
         drop: async (item: { id: number }) => {
            if (!currentFolderId) return;

            // Type-safe optimistic update
            const optimisticImages: ImageType[] = allImages.map((img) => {
               if (img.id === item.id) {
                  const parentFolderId =
                     folders.find((f) => f.id === currentFolderId)?.parentId ??
                     null;
                  return {
                     ...img,
                     folderId: parentFolderId,
                  } as ImageType; // Explicit type assertion
               }
               return img;
            });
            setOptimisticImages(optimisticImages);

            try {
               const parentFolderId = folders.find(
                  (f) => f.id === currentFolderId
               )?.parentId;
               await assignImageToFolder(item.id, parentFolderId ?? null);
            } catch (error) {
               console.error("Error moving image:", error);
               // Type-safe revert
               const revertedImages: ImageType[] = allImages.map((img) => {
                  if (img.id === item.id) {
                     return {
                        ...img,
                        folderId: currentFolderId,
                     } as ImageType; // Explicit type assertion
                  }
                  return img;
               });
               setOptimisticImages(revertedImages);
            }
            return { folderId: null };
         },
         collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop() && !!currentFolderId,
         }),
      }),
      [currentFolderId, allImages, folders]
   );

   const currentFolder = folders?.find(
      (folder) => folder.id === currentFolderId
   );

   const parentFolder = currentFolder?.parentId
      ? folders.find((f) => f.id === currentFolder.parentId)
      : null;

   return (
      <div className="flex-1 p-4">
         {currentFolderId && (
            <div
               ref={(node) =>
                  dropToParent(node) as unknown as ConnectDropTarget
               }
               className={`
                        h-8 mb-4 rounded-lg border-2 border-dashed 
                        flex items-center justify-center
                        ${
                           isOver && canDrop
                              ? "border-blue-400 bg-blue-50"
                              : "border-gray-200"
                        }
                        ${canDrop ? "cursor-pointer" : ""}
                    `}
            >
               <div className="flex items-center text-sm text-gray-500">
                  <ChevronUp className="h-4 w-4 mr-2" />
                  Drop here to move to parent folder
               </div>
            </div>
         )}

         <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
               {currentFolderId && (
                  <Button
                     variant="outline"
                     size="sm"
                     onClick={() => onNavigate(parentFolder?.id || null)}
                  >
                     <ChevronLeft className="h-4 w-4 mr-2" />
                     Back
                  </Button>
               )}
            </div>
         </div>

         <Breadcrumbs
            folders={folders}
            currentFolderId={currentFolderId}
            onNavigate={onNavigate}
         />

         <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {folders
               .filter((folder) => folder.parentId === currentFolderId)
               .map((folder) => (
                  <FolderCard
                     key={`folder-${folder.id}`}
                     folder={folder}
                     onNavigate={onNavigate}
                     onDelete={onDeleteFolder}
                     onEdit={onEditFolder}
                  />
               ))}

            {images
               .filter((image) => image.folderId === currentFolderId)
               .map((image) => (
                  <ImageCard
                     key={`image-${image.id}`}
                     image={image}
                     onDelete={onDelete}
                     onEdit={onEdit}
                  />
               ))}
         </div>
      </div>
   );
}
